import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { T } from "@/lib/testIds";
import { LogOut, LayoutDashboard, ShieldCheck, Trophy, Tags, Coffee, Users, Briefcase } from "lucide-react";
import CalsideMark from "@/components/CalsideMark";

// Stripe Payment Links per donation amount — set REACT_APP_DONATION_URL_{n}
// in the frontend .env (and on the production deploy env). Any amounts with
// an empty URL are hidden. These are plain <a href> links to Stripe-hosted
// checkout pages; the backend does NOT sit in the request path, so a bad
// Stripe key or slow Stripe response cannot produce a Cloudflare 520.
const DONATION_TIERS = [
  { amount: 3, url: process.env.REACT_APP_DONATION_URL_3 || "" },
  { amount: 5, url: process.env.REACT_APP_DONATION_URL_5 || "" },
  { amount: 10, url: process.env.REACT_APP_DONATION_URL_10 || "" },
  { amount: 20, url: process.env.REACT_APP_DONATION_URL_20 || "" },
  { amount: 30, url: process.env.REACT_APP_DONATION_URL_30 || "" },
  { amount: 50, url: process.env.REACT_APP_DONATION_URL_50 || "" },
].filter((t) => t.url);

export default function Layout({ children, wide = false }) {
  const { user, logout, isSuperAdmin, hasPracticeOpsAccess } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const onLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <div className="min-h-screen bg-brand-bone">
      <header className="sticky top-0 z-30 backdrop-blur-md bg-brand-bone/85 border-b border-brand-linen">
        <div className={`${wide ? "max-w-7xl" : "max-w-6xl"} mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center gap-2 justify-between`}>
          <Link to={user ? "/dashboard" : "/"} data-testid={T.navBrand} className="flex items-center gap-2.5 group flex-shrink-0">
            <CalsideMark size={32} className="flex-shrink-0 transition-transform group-hover:-rotate-2 sm:w-9 sm:h-9" />
            <div className="leading-tight">
              <div className="font-serif text-lg sm:text-xl text-brand-ink whitespace-nowrap">Calside</div>
              <div className="hidden sm:block text-[10px] uppercase tracking-[0.25em] text-brand-muted truncate">Practice training for UK general practice</div>
            </div>
          </Link>

          <nav className="flex items-center gap-0.5 sm:gap-2 text-sm flex-shrink min-w-0 overflow-x-auto no-scrollbar">
            {user ? (
              <>
                <Link to="/dashboard" data-testid={T.navDashboard}
                  className={`px-2 sm:px-3 py-2 rounded-full flex items-center gap-1.5 transition-colors ${location.pathname === "/dashboard" ? "bg-brand-ink text-brand-bone" : "text-brand-secondary hover:text-brand-ink hover:bg-brand-linen"}`}>
                  <LayoutDashboard className="w-4 h-4" strokeWidth={1.5} /> <span className="hidden sm:inline">Dashboard</span>
                </Link>
                <Link to="/leaderboard" data-testid="nav-leaderboard"
                  className={`px-2 sm:px-3 py-2 rounded-full flex items-center gap-1.5 transition-colors ${location.pathname.startsWith("/leaderboard") ? "bg-brand-ink text-brand-bone" : "text-brand-secondary hover:text-brand-ink hover:bg-brand-linen"}`}>
                  <Trophy className="w-4 h-4" strokeWidth={1.5} /> <span className="hidden sm:inline">Leaderboard</span>
                </Link>
                {hasPracticeOpsAccess && (
                  <Link to="/practice-ops" data-testid="nav-practice-ops"
                    className={`px-2 sm:px-3 py-2 rounded-full flex items-center gap-1.5 transition-colors ${location.pathname.startsWith("/practice-ops") ? "bg-brand-ink text-brand-bone" : "text-brand-secondary hover:text-brand-ink hover:bg-brand-linen"}`}
                    title="Practice Ops — deterministic workflows for workforce and governance">
                    <Briefcase className="w-4 h-4" strokeWidth={1.5} /> <span className="hidden sm:inline">Practice Ops</span>
                  </Link>
                )}
                {user.role === "admin" && (
                  <Link to="/admin" data-testid={T.navAdmin}
                    className={`px-2 sm:px-3 py-2 rounded-full flex items-center gap-1.5 transition-colors ${location.pathname.startsWith("/admin") ? "bg-brand-ink text-brand-bone" : "text-brand-secondary hover:text-brand-ink hover:bg-brand-linen"}`}>
                    <ShieldCheck className="w-4 h-4" strokeWidth={1.5} /> <span className="hidden sm:inline">Admin</span>
                  </Link>
                )}
                {isSuperAdmin && (
                  <Link to="/super/tags" data-testid="nav-super"
                    className={`px-2 sm:px-3 py-2 rounded-full flex items-center gap-1.5 transition-colors ${location.pathname.startsWith("/super/tags") ? "bg-brand-ink text-brand-bone" : "text-brand-muted hover:text-brand-ink hover:bg-brand-linen"}`}
                    title="Super admin — tag editor">
                    <Tags className="w-4 h-4" strokeWidth={1.5} /> <span className="hidden sm:inline">Tags</span>
                  </Link>
                )}
                {isSuperAdmin && (
                  <Link to="/super/pms" data-testid="nav-super-pms"
                    className={`px-2 sm:px-3 py-2 rounded-full flex items-center gap-1.5 transition-colors ${location.pathname.startsWith("/super/pms") ? "bg-brand-ink text-brand-bone" : "text-brand-muted hover:text-brand-ink hover:bg-brand-linen"}`}
                    title="Super admin — registered Practice Managers">
                    <Users className="w-4 h-4" strokeWidth={1.5} /> <span className="hidden sm:inline">PMs</span>
                  </Link>
                )}
                <div className="hidden md:flex items-center gap-3 pl-3 ml-2 border-l border-brand-linen">
                  <div className="text-right leading-tight">
                    <div className="text-brand-ink font-medium">{user.name}</div>
                    <div className="text-[11px] uppercase tracking-widest text-brand-muted">{user.role}</div>
                  </div>
                </div>
                <button onClick={onLogout} data-testid={T.navLogout}
                  className="p-2 rounded-full text-brand-secondary hover:text-brand-accent hover:bg-brand-linen transition-colors flex-shrink-0"
                  aria-label="Log out">
                  <LogOut className="w-4 h-4" strokeWidth={1.5} />
                </button>
              </>
            ) : (
              <>
                <Link to="/login" data-testid={T.navLogin} className="px-3 sm:px-4 py-2 rounded-full text-brand-secondary hover:text-brand-ink hover:bg-brand-linen whitespace-nowrap">Log in</Link>
                <Link to="/register" data-testid={T.navRegister} className="px-3 sm:px-4 py-2 rounded-full bg-brand-ink text-brand-bone hover:bg-brand-inkHover transition-colors whitespace-nowrap text-xs sm:text-sm">Get started</Link>
              </>
            )}
          </nav>
        </div>
      </header>

      <main className={`${wide ? "max-w-7xl" : "max-w-6xl"} mx-auto px-4 sm:px-6 py-6 sm:py-10 overflow-x-hidden`}>{children}</main>

      <footer className="border-t border-brand-linen mt-16">
        <div className="max-w-6xl mx-auto px-6 py-8 flex flex-col sm:flex-row justify-between items-center gap-4 text-sm text-brand-muted">
          <div className="font-serif text-brand-ink text-base">Calside — practice training for UK general practice</div>
          <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1">
            <span
              data-testid="footer-free-badge"
              className="inline-flex items-center gap-1.5 bg-brand-accent/10 text-brand-accent border border-brand-accent/25 rounded-full px-2.5 py-0.5 text-[11px] font-medium tracking-wide"
            >
              100% free — always
            </span>
            <span className="text-brand-linen hidden sm:inline">·</span>
            <span className="text-center">Created by Grant Cameron Anthony · British English · © 2026</span>
            <span className="text-brand-linen hidden sm:inline">·</span>
            <Link to="/privacy" className="hover:text-brand-ink transition-colors whitespace-nowrap" data-testid="footer-privacy-link">Privacy</Link>
            {DONATION_TIERS.length > 0 && (
              <>
                <span className="text-brand-linen hidden sm:inline">·</span>
                <span className="inline-flex items-center gap-1.5 text-brand-muted whitespace-nowrap">
                  <Coffee className="w-3.5 h-3.5" strokeWidth={1.5} />
                  Keep it afloat
                </span>
                <span className="inline-flex items-center gap-1">
                  {DONATION_TIERS.map((tier) => (
                    <a
                      key={tier.amount}
                      href={tier.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`footer-donate-${tier.amount}`}
                      className="inline-flex items-center justify-center rounded-full border border-brand-linen bg-white/60 px-2.5 py-0.5 text-[11px] font-medium text-brand-secondary hover:text-brand-accent hover:border-brand-accent/40 hover:bg-brand-accent/5 transition-colors"
                      title={`Donate £${tier.amount} via Stripe`}
                    >
                      £{tier.amount}
                    </a>
                  ))}
                </span>
              </>
            )}
          </div>
        </div>
      </footer>
    </div>
  );
}
